import React, { useEffect, useMemo, useState } from "react";
import { motion } from "framer-motion";
import { ArrowUpRight } from "lucide-react";
import api from "@/lib/api";
import { INSIGHTS } from "@/data/site";
import { SITE } from "@/constants/testIds";

// Editorial cover fallbacks per legacy insight slug (Unsplash — used only when
// Substack RSS is unavailable and we fall back to hardcoded copy).
const COVERS = {
  "stop-starting-with-the-camera":
    "https://images.unsplash.com/photo-1492691527719-9d1e07e534b4?auto=format&fit=crop&w=1400&q=70",
  "employer-branding-is-not-a-film":
    "https://images.unsplash.com/photo-1517245386807-bb43f82c33c4?auto=format&fit=crop&w=1400&q=70",
  "the-quiet-revival-of-corporate-storytelling":
    "https://images.unsplash.com/photo-1518770660439-4636190af475?auto=format&fit=crop&w=1400&q=70",
  "founders-on-camera":
    "https://images.unsplash.com/photo-1521737604893-d14cc237f11d?auto=format&fit=crop&w=1400&q=70",
  "the-business-of-production":
    "https://images.unsplash.com/photo-1485846234645-a62644f84728?auto=format&fit=crop&w=1400&q=70",
};

const FALLBACK =
  "https://images.unsplash.com/photo-1485846234645-a62644f84728?auto=format&fit=crop&w=1400&q=70";

/** Format ISO-like date to "12 Jul 2026". */
function prettyDate(input) {
  if (!input) return "";
  const d = new Date(input);
  if (Number.isNaN(d.getTime())) return input;
  return d.toLocaleDateString("en-GB", { day: "numeric", month: "short", year: "numeric" });
}

/** Rough reading-time from summary length (Substack RSS doesn't ship this). */
function readTime(summary) {
  const words = (summary || "").split(/\s+/).filter(Boolean).length;
  const min = Math.max(3, Math.round(words / 220));
  return `${min} min read`;
}

/**
 * Normalize Substack RSS entries into the shape the rest of this file expects.
 */
function fromSubstack(post) {
  return {
    slug: post.link,           // external link doubles as slug
    external: true,            // marks the card as an outbound Substack link
    title: post.title,
    excerpt: post.summary,
    category: "Field Notes",   // Substack API doesn't expose our own categories
    date: prettyDate(post.published),
    readTime: readTime(post.summary),
    image: post.image,
    link: post.link,
  };
}

export default function Insights() {
  const [remote, setRemote] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    let cancelled = false;
    api.get("/blog/posts").then((r) => {
      if (cancelled) return;
      const posts = r.data?.posts || [];
      setRemote(posts.length > 0 ? posts.map(fromSubstack) : []);
    }).catch(() => {
      if (!cancelled) setRemote([]);
    }).finally(() => {
      if (!cancelled) setLoading(false);
    });
    return () => { cancelled = true; };
  }, []);

  // Use Substack posts when available; fall back to hardcoded insights so the
  // section is never empty on first paint or if the RSS fetch failed.
  const items = useMemo(() => {
    if (remote && remote.length > 0) return remote;
    return INSIGHTS.map((p) => ({ ...p, external: false, image: COVERS[p.slug] || FALLBACK }));
  }, [remote]);

  if (items.length === 0) return null;
  const [hero, ...rest] = items;

  const cardHref = (p) => (p.external ? p.link : `/insights/${p.slug}`);
  const cardProps = (p) => (p.external
    ? { href: cardHref(p), target: "_blank", rel: "noopener noreferrer" }
    : { href: cardHref(p) }
  );

  return (
    <section data-testid={SITE.insightsSection} className="bg-bone text-ink py-20 md:py-24 lg:py-28">
      <div className="px-6 md:px-12 lg:px-24">
        <div className="flex flex-col md:flex-row md:items-end md:justify-between mb-14 md:mb-20 gap-6">
          <div>
            <p className="eyebrow text-ink/60 mb-5">
              ◇ Field Notes — Editorial
              {remote && remote.length > 0 && (
                <span className="ml-3 inline-flex items-center gap-1.5 text-relatable">
                  <span className="w-1.5 h-1.5 bg-relatable rounded-full animate-pulse" />
                  Live from Substack
                </span>
              )}
            </p>
            <h2 className="font-display text-5xl md:text-6xl lg:text-7xl tracking-tighter font-medium leading-[0.95]">
              Notes from the<br/>production floor.
            </h2>
          </div>
          <p className="max-w-md text-ink/70 text-base md:text-lg leading-relaxed">
            Long-form essays on storytelling, employer branding, founder visibility, and the business of production. Published on{" "}
            <a
              href="https://rudhravishnu.substack.com/"
              target="_blank"
              rel="noopener noreferrer"
              data-testid="insights-substack-link"
              className="text-ink link-line"
            >
              Substack
            </a>
            {loading ? " · loading latest…" : ""}
          </p>
        </div>

        {/* Hero article */}
        <motion.div
          initial={{ opacity: 0, y: 24 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.6 }}
          className="border-t border-ink/15 pt-10 md:pt-12 mb-12 md:mb-16"
        >
          <a
            {...cardProps(hero)}
            className="group block"
            data-testid={`insight-hero-${hero.external ? "substack" : hero.slug}`}
          >
            <div className="grid grid-cols-12 gap-6 md:gap-10 items-center">
              <div className="col-span-12 md:col-span-7 relative overflow-hidden bg-ink/10">
                <div className="relative w-full" style={{ aspectRatio: "16 / 9" }}>
                  <img
                    src={hero.image || FALLBACK}
                    alt={hero.title}
                    className="card-img absolute inset-0 w-full h-full object-cover"
                    loading="lazy"
                    onError={(e) => { e.currentTarget.src = FALLBACK; }}
                  />
                </div>
              </div>
              <div className="col-span-12 md:col-span-5">
                <p className="eyebrow text-ink/60">{hero.category} · {hero.date}</p>
                <h3 className="mt-4 font-display text-4xl md:text-5xl lg:text-6xl tracking-tighter font-medium leading-[1] group-hover:text-relatable transition-colors">
                  {hero.title}
                </h3>
                {hero.excerpt && (
                  <p className="mt-6 text-ink/70 text-base md:text-lg leading-relaxed line-clamp-4">{hero.excerpt}</p>
                )}
                <span className="mt-6 inline-flex items-center gap-2 eyebrow text-ink link-line">
                  {hero.external ? "Read on Substack" : "Read the piece"} <ArrowUpRight className="w-4 h-4" />
                </span>
              </div>
            </div>
          </a>
        </motion.div>

        {/* Rest */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-px bg-ink/15 border-t border-ink/15">
          {rest.slice(0, 8).map((p, i) => (
            <motion.div
              key={p.slug}
              initial={{ opacity: 0, y: 18 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ delay: i * 0.05, duration: 0.5 }}
              className="bg-bone group hover:bg-ink hover:text-bone transition-colors duration-500"
            >
              <a
                {...cardProps(p)}
                className="block p-6 md:p-8"
                data-testid={`insight-card-${i}`}
              >
                <div className="relative overflow-hidden mb-6">
                  <div className="relative w-full" style={{ aspectRatio: "4 / 3" }}>
                    <img
                      src={p.image || FALLBACK}
                      alt={p.title}
                      className="card-img absolute inset-0 w-full h-full object-cover"
                      loading="lazy"
                      onError={(e) => { e.currentTarget.src = FALLBACK; }}
                    />
                  </div>
                </div>
                <p className="eyebrow text-ink/60 group-hover:text-bone/70 transition-colors">{p.category}</p>
                <h3 className="mt-3 font-display text-xl md:text-2xl tracking-tight font-medium leading-[1.15] line-clamp-3">
                  {p.title}
                </h3>
                <div className="mt-8 flex items-center justify-between eyebrow text-ink/55 group-hover:text-bone/70 transition-colors">
                  <span>{p.date}</span>
                  <span>{p.readTime}</span>
                </div>
              </a>
            </motion.div>
          ))}
        </div>

        {remote && remote.length > 0 && (
          <div className="mt-16 md:mt-20 border border-ink/15 bg-ink text-bone" data-testid="insights-subscribe-block">
            <div className="grid grid-cols-1 md:grid-cols-12 gap-8 md:gap-12 p-8 md:p-12 lg:p-16 items-center">
              <div className="md:col-span-6 lg:col-span-7">
                <p className="eyebrow text-relatable mb-4">
                  <span className="inline-flex items-center gap-2">
                    <span className="w-1.5 h-1.5 bg-relatable rounded-full animate-pulse" />
                    Newsletter · Weekly-ish
                  </span>
                </p>
                <h3 className="font-display text-4xl md:text-5xl lg:text-6xl tracking-tighter font-medium leading-[0.95] text-bone">
                  Notes in your<br/>inbox &mdash; the moment they land.
                </h3>
                <p className="mt-6 max-w-md text-fog text-base md:text-lg leading-relaxed">
                  Long-form essays on brand storytelling, employer branding, founder visibility and the business of production. No listicles. No fluff. Free.
                </p>
                <ul className="mt-6 space-y-2 text-fog text-sm">
                  <li>&middot; New essay every 1&ndash;2 weeks</li>
                  <li>&middot; Case-studies of campaigns we&rsquo;re watching</li>
                  <li>&middot; No spam, unsubscribe anytime</li>
                </ul>
              </div>
              <div className="md:col-span-6 lg:col-span-5">
                <div className="bg-bone p-1" data-testid="insights-substack-embed">
                  {/* Official Substack subscription form. Handles email capture,
                      double opt-in, and drops the subscriber straight into the
                      publication — no bridge server or webhook needed. */}
                  <iframe
                    src="https://rudhravishnu.substack.com/embed"
                    title="Subscribe to Rudhra's Substack"
                    width="100%"
                    height="320"
                    style={{ border: "0", background: "#F5F4F0" }}
                    frameBorder="0"
                    scrolling="no"
                    loading="lazy"
                  />
                </div>
                <p className="mt-3 text-fog text-xs eyebrow">
                  Powered by{" "}
                  <a
                    href="https://rudhravishnu.substack.com/"
                    target="_blank"
                    rel="noopener noreferrer"
                    className="text-bone link-line"
                  >
                    Substack
                  </a>
                </p>
              </div>
            </div>
          </div>
        )}

        {remote && remote.length > 0 && (
          <div className="mt-16 text-center">
            <a
              href="https://rudhravishnu.substack.com/"
              target="_blank"
              rel="noopener noreferrer"
              data-testid="insights-see-all-substack"
              className="inline-flex items-center gap-3 px-7 py-4 border border-ink text-ink hover:bg-ink hover:text-bone eyebrow transition-colors"
            >
              Read all essays on Substack <ArrowUpRight className="w-4 h-4" />
            </a>
          </div>
        )}
      </div>
    </section>
  );
}
